import { SEGMENT_PROXY_ROUTES } from 'lib/consts/segmentProxy'

interface IsThirdPartyThrownErrorParams {
  source: string
  stack?: string
}

// Only `chrome-extension://` can appear: Datadog's stack parser silently drops frames with other
// extension schemes (`moz-extension`, `safari-web-extension`, `webkit-masked-url`), leaving the
// first app frame on top — those browsers' extension throws are unfilterable here.
const VENDOR_THROWING_FRAME_PATTERNS: RegExp[] = [
  /^chrome-extension:\/\//,
  /^https:\/\/www\.gstatic\.com\/recaptcha\//,
  ...SEGMENT_PROXY_ROUTES.map(({ prefix }) => new RegExp(`^https://[^/]+${prefix}/`)),
]

// The frame shape comes from `toStackTraceString` in @datadog/browser-core — an internal
// serialization with no format contract. If an SDK upgrade changes it, every stack stops matching
// and the filter silently fails open, so re-verify the shape when bumping the SDK.
const THROWING_FRAME_RE = /^\s*at .*? @ (.+)$/m

const getThrowingFrameUrl = (stack: string): string | undefined => THROWING_FRAME_RE.exec(stack)?.[1]?.trim()

/**
 * True when RUM auto-captured an uncaught throw whose throwing (top) frame is vendor code we can't
 * act on: a browser extension, reCAPTCHA internals, or a Segment bundle behind the first-party
 * `/seg-cdn` proxy (our origin — only the path identifies it). Only the top frame decides:
 * extensions that patch `XMLHttpRequest` leave app frames beneath their throws, while an app throw
 * invoked *by* vendor code keeps an app top frame and stays reported. `logError` reports
 * (`source: custom`) never match.
 */
export const isThirdPartyThrownError = ({ source, stack }: IsThirdPartyThrownErrorParams): boolean => {
  if (source !== 'source' || !stack) {
    return false
  }

  const throwingFrameUrl = getThrowingFrameUrl(stack)

  if (!throwingFrameUrl) {
    return false
  }

  return VENDOR_THROWING_FRAME_PATTERNS.some((pattern) => pattern.test(throwingFrameUrl))
}
